/**
 * Step "Dati azienda" del wizard signup.
 *
 * UX ottimizzata per ridurre la digitazione manuale e massimizzare la conversione:
 *  - Shortcut: campo "Cerca la tua azienda" (Google Places Autocomplete type=establishment)
 *    → al click di un risultato auto-compila ragione sociale + indirizzo + città/cap/provincia/regione.
 *  - Partita IVA + bottone "Verifica e auto-compila" (lookup VIES tramite CF lookupVatInfo).
 *  - Campo indirizzo con Autocomplete Places type=address (residual auto-compile).
 *  - Provincia: dropdown filtrato in base alla regione selezionata.
 *  - Regione: dropdown completo. Al cambio regione resetta provincia se non più valida.
 *
 * Niente più REA (rimosso 2026-05-16 per ridurre friction).
 */

import { useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { useSignup } from '../SignupContext';
import { lookupVat } from '../../../services/SignupService';
import { validateItalianVatChecksum } from '../../../utils/italianVatChecksum';
import {
  getPlaceDetails,
  searchPlaces,
  type PlaceSuggestion,
} from '../../../services/GooglePlacesService';
import {
  REGIONI_ITALIANE,
  findProvinceByRegioneNome,
  findRegioneByProvinciaSigla,
  type Provincia,
} from '../../../data/italianProvinces';
import type { SignupState } from '../types';

const fieldSx = {
  '& .MuiInputBase-root': {
    fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
    color: 'var(--ink)',
    '&::before': {
      borderBottom: '1px solid var(--dust)',
    },
    '&:hover::before': {
      borderBottom: '1px solid var(--ink) !important',
    },
    '&::after': {
      borderBottom: '2px solid var(--sun)',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: '"JetBrains Mono Variable", monospace',
    fontSize: '0.75rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--ink-soft)',
    '&.Mui-focused': { color: 'var(--sun-deep)' },
  },
  '& .MuiInput-input': { padding: '8px 0', fontSize: '1.05rem' },
  '& .MuiFormHelperText-root': {
    fontFamily: '"JetBrains Mono Variable", monospace',
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    color: 'var(--ink-soft)',
  },
};

const DEBOUNCE_MS = 300;

export default function StepDatiAzienda() {
  const { state, update } = useSignup();
  const { t } = useTranslation('signup');

  // VAT lookup
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'loading' | 'success' | 'soft_success' | 'error'>('idle');
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Places: company search shortcut
  const [companyInput, setCompanyInput] = useState('');
  const [companyOptions, setCompanyOptions] = useState<PlaceSuggestion[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);

  // Places: address autocomplete
  const [addressInput, setAddressInput] = useState(state.sede_via || '');
  const [addressOptions, setAddressOptions] = useState<PlaceSuggestion[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);

  // Debounce timers
  const companyTimer = useRef<number | null>(null);
  const addressTimer = useRef<number | null>(null);

  // Province filtered by selected region
  const provinciaOptions: Provincia[] = state.sede_regione
    ? findProvinceByRegioneNome(state.sede_regione)
    : [];

  // True quando i campi anagrafici principali sono gia tutti pieni
  // (es. dopo che l'utente ha usato la ricerca aziende Google).
  // In questo caso il bottone diventa solo "Verifica" (non sovrascrive).
  const fieldsAlreadyFilled = !!(
    state.ragione_sociale &&
    state.sede_via &&
    state.sede_citta &&
    state.sede_cap &&
    state.sede_provincia &&
    state.sede_regione
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleVatLookup = async () => {
    if (!state.partita_iva || state.partita_iva.length < 4) return;

    // Snapshot del flag "campi gia pieni" PRIMA della chiamata.
    const shouldOnlyVerify = fieldsAlreadyFilled;

    // Step 1: validazione checksum locale (immediato, no rete)
    const checksumOk = validateItalianVatChecksum(state.partita_iva);
    if (!checksumOk) {
      setLookupStatus('error');
      setLookupError('lookup.partita_iva.invalid_checksum');
      return;
    }

    setLookupStatus('loading');
    setLookupError(null);

    try {
      const result = await lookupVat(state.partita_iva, 'IT');
      if (result.ok) {
        if (!shouldOnlyVerify) {
          const patch: Partial<SignupState> = {};
          if (result.ragione_sociale) patch.ragione_sociale = result.ragione_sociale;
          if (result.parsed?.via) {
            patch.sede_via = result.parsed.via;
            setAddressInput(result.parsed.via);
          }
          if (result.parsed?.cap) patch.sede_cap = result.parsed.cap;
          if (result.parsed?.citta) patch.sede_citta = result.parsed.citta;
          if (result.parsed?.provincia) patch.sede_provincia = result.parsed.provincia;
          if (result.parsed?.regione) patch.sede_regione = result.parsed.regione;
          update(patch);
        }
        setLookupStatus('success');
      } else {
        // VIES ha risposto con error_code specifico.
        // Casi: vies_unavailable / service_unavailable → fallback al checksum
        // (gia validato sopra come OK → soft_success). Altri errori (es. not_valid) → error duro.
        const code = result.error_code;
        if (code === 'lookup.service_unavailable' || code === 'lookup.vies_unavailable' || code === 'lookup.internal_error') {
          // Fallback al checksum: formato OK ma verifica completa non fatta
          setLookupStatus('soft_success');
          setLookupError(null);
        } else {
          setLookupStatus('error');
          setLookupError(code);
        }
      }
    } catch {
      // Network error → fallback al checksum (gia OK)
      setLookupStatus('soft_success');
      setLookupError(null);
    }
  };

  const handleCompanyInput = (v: string) => {
    setCompanyInput(v);
    if (companyTimer.current) window.clearTimeout(companyTimer.current);
    if (!v || v.length < 2) {
      setCompanyOptions([]);
      return;
    }
    companyTimer.current = window.setTimeout(async () => {
      setCompanyLoading(true);
      try {
        const results = await searchPlaces(v, ['establishment']);
        setCompanyOptions(results);
      } finally {
        setCompanyLoading(false);
      }
    }, DEBOUNCE_MS);
  };

  const applyPlaceDetails = (
    details: Awaited<ReturnType<typeof getPlaceDetails>>,
    includeName: boolean,
  ) => {
    if (!details) return;
    const patch: Partial<SignupState> = {};
    if (includeName && details.name) patch.ragione_sociale = details.name;
    if (details.via) {
      patch.sede_via = details.via;
      setAddressInput(details.via);
    }
    if (details.cap) patch.sede_cap = details.cap;
    if (details.citta) patch.sede_citta = details.citta;
    if (details.provinciaSigla) patch.sede_provincia = details.provinciaSigla;
    if (details.regione) {
      patch.sede_regione = details.regione;
    } else if (details.provinciaSigla) {
      const r = findRegioneByProvinciaSigla(details.provinciaSigla);
      if (r) patch.sede_regione = r.nome;
    }
    update(patch);
  };

  const handleCompanySelect = async (suggestion: PlaceSuggestion | null) => {
    if (!suggestion) return;
    // Sostituisci il contenuto del campo "Cerca azienda" con solo il nome,
    // senza il resto dell'indirizzo (che va negli altri campi appositi)
    setCompanyInput(suggestion.mainText || suggestion.fullText);
    const details = await getPlaceDetails(suggestion.placeId);
    applyPlaceDetails(details, true);
  };

  const handleAddressInput = (v: string) => {
    setAddressInput(v);
    update({ sede_via: v });
    if (addressTimer.current) window.clearTimeout(addressTimer.current);
    if (!v || v.length < 2) {
      setAddressOptions([]);
      return;
    }
    addressTimer.current = window.setTimeout(async () => {
      setAddressLoading(true);
      try {
        const results = await searchPlaces(v, ['address']);
        setAddressOptions(results);
      } finally {
        setAddressLoading(false);
      }
    }, DEBOUNCE_MS);
  };

  const handleAddressSelect = async (suggestion: PlaceSuggestion | null) => {
    if (!suggestion) return;
    const details = await getPlaceDetails(suggestion.placeId);
    applyPlaceDetails(details, false);
  };

  const handleRegioneChange = (nome: string) => {
    const patch: Partial<SignupState> = { sede_regione: nome };
    // Se la provincia corrente non appartiene alla nuova regione, resetta
    const newProv = findProvinceByRegioneNome(nome);
    if (state.sede_provincia && !newProv.some((p) => p.sigla === state.sede_provincia)) {
      patch.sede_provincia = '';
    }
    update(patch);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Stack spacing={4}>
      <Box>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Fraunces Variable", Georgia, serif',
            fontSize: { xs: '2.4rem', md: '3.2rem' },
            letterSpacing: '-0.02em',
            lineHeight: 1,
            mb: 2,
          }}
        >
          {t('step_azienda.titolo')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: 'var(--ink-soft)', maxWidth: 540, fontSize: '1.05rem' }}
        >
          {t('step_azienda.descrizione')}
        </Typography>
      </Box>

      <Grid container spacing={3.5}>
        {/* Shortcut: cerca azienda su Google Places */}
        <Grid size={{ xs: 12 }}>
          <Autocomplete<PlaceSuggestion, false, false, true>
            freeSolo
            options={companyOptions}
            inputValue={companyInput}
            filterOptions={(x) => x}
            onInputChange={(_, v) => handleCompanyInput(v)}
            onChange={(_, v) => {
              if (v && typeof v !== 'string') void handleCompanySelect(v);
            }}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.mainText)}
            loading={companyLoading}
            isOptionEqualToValue={(opt, val) =>
              typeof opt !== 'string' && typeof val !== 'string' && opt.placeId === val.placeId
            }
            renderOption={(props, option) => (
              <li {...props} key={option.placeId}>
                <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                  <Box sx={{
                    fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: 'var(--ink)',
                  }}>
                    {option.mainText}
                  </Box>
                  {option.secondaryText && (
                    <Box sx={{
                      fontFamily: '"JetBrains Mono Variable", monospace',
                      fontSize: '0.72rem',
                      color: 'var(--ink-soft)',
                      mt: 0.3,
                    }}>
                      {option.secondaryText}
                    </Box>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('step_azienda.cerca_azienda')}
                variant="standard"
                helperText={t('step_azienda.cerca_azienda_helper')}
                sx={fieldSx}
              />
            )}
          />
        </Grid>

        {/* Partita IVA + verify */}
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField
            label={t('step_azienda.partita_iva')}
            fullWidth
            required
            variant="standard"
            value={state.partita_iva ?? ''}
            onChange={(e) => {
              update({ partita_iva: e.target.value });
              if (lookupStatus !== 'idle') {
                setLookupStatus('idle');
                setLookupError(null);
              }
            }}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleVatLookup}
            disabled={
              !state.partita_iva ||
              state.partita_iva.length < 4 ||
              lookupStatus === 'loading'
            }
            sx={{
              fontFamily: '"JetBrains Mono Variable", monospace',
              fontSize: '0.72rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              borderRadius: 0,
              borderColor: lookupStatus === 'success' ? 'var(--sun-deep)'
                : lookupStatus === 'soft_success' ? '#C49A2E'
                : 'var(--ink)',
              color: lookupStatus === 'success' ? 'var(--sun-deep)'
                : lookupStatus === 'soft_success' ? '#C49A2E'
                : 'var(--ink)',
              padding: { xs: '12px 16px', md: '10px 16px' },
              minHeight: 44,
              mb: 0.5,
              '&:hover': {
                background: 'var(--ink)',
                color: 'var(--bg)',
                borderColor: 'var(--ink)',
              },
            }}
          >
            {lookupStatus === 'loading' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
                <span>{t('step_azienda.lookup_loading')}</span>
              </Box>
            ) : lookupStatus === 'success' ? (
              fieldsAlreadyFilled ? t('step_azienda.verify_success') : t('step_azienda.lookup_success')
            ) : lookupStatus === 'soft_success' ? (
              t('step_azienda.soft_success')
            ) : (
              fieldsAlreadyFilled ? t('step_azienda.verify_button') : t('step_azienda.lookup_button')
            )}
          </Button>
        </Grid>
        {lookupStatus === 'error' && lookupError && (
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ color: '#B33A2E', fontSize: '0.85rem', fontFamily: '"JetBrains Mono Variable", monospace' }}>
              {t(
                fieldsAlreadyFilled && (lookupError === 'lookup.service_unavailable' || lookupError === 'lookup.vies_unavailable')
                  ? 'errors.lookup.service_unavailable_filled'
                  : `errors.${lookupError}`,
                { defaultValue: t('errors.signup.unknown_error') },
              )}
            </Typography>
          </Grid>
        )}
        {lookupStatus === 'soft_success' && (
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ color: '#C49A2E', fontSize: '0.85rem', fontFamily: '"JetBrains Mono Variable", monospace' }}>
              {t('step_azienda.soft_success_info')}
            </Typography>
          </Grid>
        )}

        {/* Ragione sociale */}
        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('step_azienda.ragione_sociale')}
            fullWidth
            required
            variant="standard"
            value={state.ragione_sociale ?? ''}
            onChange={(e) => update({ ragione_sociale: e.target.value })}
            sx={fieldSx}
          />
        </Grid>

        {/* Indirizzo con autocomplete Places */}
        <Grid size={{ xs: 12 }}>
          <Autocomplete<PlaceSuggestion, false, false, true>
            freeSolo
            options={addressOptions}
            inputValue={addressInput}
            filterOptions={(x) => x}
            onInputChange={(_, v) => handleAddressInput(v)}
            onChange={(_, v) => {
              if (v && typeof v !== 'string') void handleAddressSelect(v);
            }}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.fullText)}
            loading={addressLoading}
            isOptionEqualToValue={(opt, val) =>
              typeof opt !== 'string' && typeof val !== 'string' && opt.placeId === val.placeId
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('step_azienda.indirizzo_sede')}
                required
                variant="standard"
                sx={fieldSx}
              />
            )}
          />
        </Grid>

        {/* Città */}
        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('step_azienda.citta')}
            fullWidth
            required
            variant="standard"
            value={state.sede_citta ?? ''}
            onChange={(e) => update({ sede_citta: e.target.value })}
            sx={fieldSx}
          />
        </Grid>

        {/* CAP + Provincia (provincia dropdown filtrato per regione) */}
        <Grid size={{ xs: 6 }}>
          <TextField
            label={t('step_azienda.cap')}
            fullWidth
            required
            variant="standard"
            value={state.sede_cap ?? ''}
            onChange={(e) => update({ sede_cap: e.target.value })}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Autocomplete<Provincia, false, false, false>
            options={provinciaOptions}
            value={provinciaOptions.find((p) => p.sigla === state.sede_provincia) || null}
            onChange={(_, v) => update({ sede_provincia: v?.sigla ?? '' })}
            getOptionLabel={(o) => `${o.sigla} — ${o.nome}`}
            isOptionEqualToValue={(opt, val) => opt.sigla === val.sigla}
            disabled={!state.sede_regione}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('step_azienda.provincia')}
                required
                variant="standard"
                helperText={!state.sede_regione ? t('step_azienda.provincia_helper') : ''}
                sx={fieldSx}
              />
            )}
          />
        </Grid>

        {/* Regione (dropdown completo) */}
        <Grid size={{ xs: 12 }}>
          <Autocomplete<string, false, false, false>
            options={REGIONI_ITALIANE.map((r) => r.nome)}
            value={state.sede_regione || null}
            onChange={(_, v) => handleRegioneChange(v ?? '')}
            isOptionEqualToValue={(opt, val) => opt === val}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('step_azienda.regione')}
                required
                variant="standard"
                sx={fieldSx}
              />
            )}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
